import axios from "axios";
import parse from "html-react-parser";
import moment from "moment";
import { useState } from "react";
import { Accordion, Container, Row, Col, Button } from "react-bootstrap";

import {
  ACCESS_LEVELS,
  AUTH_ROLES,
  PERMISSION_AREAS,
} from "api/common/types/auth.types";
import {
  useDeleteTqVoteMutation,
  useTqVotesByIdQuery,
} from "api/memberPortal/voting/tqVotes/tqVotes.api";
import HTMLEditor from "components/UI/HTMLEditor";
import { useAuth } from "hooks/useAuth";
import { checkPermissions } from "utils/permissions";
import { getISOCurrentDate, getCurrentDate } from "utils/timeUtils";

import TQActiveVoting from "./TQVoting/TQActiveVoting";
import TQReadOnlyVoting from "./TQVoting/TQReadOnlyVoting";

const TQResponseAccordion = ({
  technicalQuery,
  trrtMember,
  updateTechnicalHistory,
  updateTechnicalQuery,
  fetchTQNotes,
}) => {
  const { authToken, csrfToken, currentUser, permissions } = useAuth();

  const { data: votes = [] } = useTqVotesByIdQuery(technicalQuery.tq_id, {
    skip: !technicalQuery.tq_id,
  });

  const currentUserVote =
    (votes && votes.find((vote) => vote.voter.id === currentUser.id)) ||
    undefined;

  const [triggerDeleteTq] = useDeleteTqVoteMutation();

  const [response, setResponse] = useState({});
  const [saveDisabled, setSaveDisabled] = useState(true);

  const handleChange = (e) => {
    setSaveDisabled(false);
    setResponse({ ...response, [e.target.name]: e.target.value });
  };

  const handleResetVotes = async () => {
    votes.forEach((vote) => {
      triggerDeleteTq(vote.id);
    });
    await updateTechnicalHistory({ reason_for_update: "Reset Votes" });
  };

  const handleSaveResponse = async () => {
    setSaveDisabled(true);
    let data = {
      ...response,
    };

    if (technicalQuery?.response_by === null) {
      data["response_by"] = currentUser?.id;
      data["response_on"] = getCurrentDate();
    }

    if (technicalQuery?.status === "Proposed") {
      data["status"] = "In Progress";
    }

    await updateTechnicalQuery(data);
    await updateTechnicalHistory({
      reason_for_update: "Updated Draft Response",
    });
    handleAddNote(data);
  };

  const handleAddNote = (response) => {
    axios
      .post(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}note/`,
        {
          visible_to_validators: true,
          visible_to_labs: false,
          note: response?.response,
          title: "Updated Draft Response",
          note_type: "technical_query",
          note_type_id: technicalQuery?.tq_id,
          submitted_on: getISOCurrentDate(),
        },
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then(() => {
        fetchTQNotes();
      })
      .catch((error) => {
        console("Unable to add note: " + error);
      });
  };

  const handleOpenForVoting = async () => {
    await updateTechnicalQuery({
      ...response,
      status: "Resolution Proposed",
      send_email: true,
    });
    await updateTechnicalHistory({ reason_for_update: "Opened for Voting" });
  };

  const handleSendToCustomer = async () => {
    let data = {
      status: "Resolved",
      official_response: technicalQuery?.response,
      official_response_by: currentUser?.id,
      official_response_on: getCurrentDate(),
      send_email: true,
    };
    await updateTechnicalQuery(data);
    await updateTechnicalHistory({
      reason_for_update: "Sent Official Response",
    });
  };

  const userIsLab = permissions.role_type === AUTH_ROLES.LAB;
  const userIsNiap = permissions.role_type === AUTH_ROLES.NIAP;
  const userIsSme = permissions.role_type === AUTH_ROLES.SME;
  const userIsValidator = permissions.role_type === AUTH_ROLES.VALIDATOR;

  const userCanUpdateTqs = checkPermissions({
    permissions,
    area: PERMISSION_AREAS.TECHNICAL_QUESTIONS,
    access: ACCESS_LEVELS.UPDATE,
  });

  return (
    <>
      {!userIsLab && (
        <Accordion>
          {technicalQuery?.status === "Rejected" ? (
            <Accordion>
              <Accordion.Item eventKey="1">
                <Accordion.Header className="border-0">
                  <h6 className="text-primary fw-bold">REJECTION RATIONALE:</h6>
                </Accordion.Header>
                <Accordion.Body>{technicalQuery?.response}</Accordion.Body>
              </Accordion.Item>
            </Accordion>
          ) : (
            <Accordion.Item eventKey="1">
              <Accordion.Header className="border-0">
                <h6 className="text-primary fw-bold">DRAFT RESPONSE:</h6>
              </Accordion.Header>
              <Accordion.Body>
                {userCanUpdateTqs &&
                !["TD Required", "Resolved", "TD Proposed"].includes(
                  technicalQuery?.status,
                ) ? (
                  <Container>
                    <Row>
                      <div className="d-flex justify-content-end">
                        {technicalQuery?.response_by && (
                          <p className="mx-2">
                            Original Response by{" "}
                            <span className="fst-italic">
                              {technicalQuery?.response_by?.first_name}{" "}
                              {technicalQuery?.response_by?.last_name}
                            </span>{" "}
                            on{" "}
                            <span className="fst-italic">
                              {moment(technicalQuery?.response_on).format(
                                "MM/DD/YYYY",
                              )}
                            </span>
                          </p>
                        )}

                        {technicalQuery?.status === "Resolution Proposed" && (
                          <div className="d-flex justify-content-between">
                            <TQActiveVoting
                              technicalQuery={technicalQuery}
                              updateTechnicalHistory={updateTechnicalHistory}
                              dashboardTable={false}
                            />
                          </div>
                        )}
                      </div>
                    </Row>
                    <Row>
                      <Col>
                        <HTMLEditor
                          name="response"
                          handleChange={handleChange}
                          setIsValid={() => {}}
                          isValid={true}
                          defaultValue={technicalQuery?.response}
                        />
                      </Col>
                    </Row>
                    <Row className="mt-3 mb-3 d-flex justify-content-end">
                      <Col xs={3} className="w-auto">
                        <Button
                          id={"save-response-button"}
                          variant="success"
                          onClick={handleSaveResponse}
                          disabled={saveDisabled}
                        >
                          Save
                        </Button>
                      </Col>
                      <Col xs={3} className="w-auto">
                        {technicalQuery?.status !== "Resolved" &&
                          userCanUpdateTqs &&
                          userIsNiap && (
                            <Button
                              id={"send-official-response-button"}
                              variant="primary"
                              disabled={
                                //if not niap, needs 3 upvotes, else only 1 upvote
                                votes.filter((vote) => vote.approved === true)
                                  .length < (userIsNiap ? 1 : 3)
                              }
                              onClick={handleSendToCustomer}
                            >
                              Send Official Response
                            </Button>
                          )}
                      </Col>
                      {(userIsNiap || userIsSme || trrtMember) &&
                        votes.length === 0 &&
                        technicalQuery?.status !== "Resolution Proposed" && (
                          <Col xs={3} className="w-auto">
                            <Button
                              id={"open-for-voting-button"}
                              variant="warning"
                              onClick={() => handleOpenForVoting()}
                              disabled={!technicalQuery?.response}
                            >
                              Open For Voting
                            </Button>
                          </Col>
                        )}
                      {votes.length > 0 && userIsNiap && (
                        <Col xs={3} className="w-auto">
                          <Button variant="danger" onClick={handleResetVotes}>
                            Reset Votes
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Container>
                ) : (
                  <Container>
                    {(userIsNiap || trrtMember || userIsValidator) && (
                      <TQReadOnlyVoting
                        votes={votes}
                        currentUserVote={currentUserVote}
                      />
                    )}
                    {technicalQuery?.response &&
                      parse(technicalQuery?.response)}
                  </Container>
                )}
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>
      )}
      <Accordion>
        <Accordion.Item eventKey="1">
          <Accordion.Header className="border-0">
            <h6 className="text-primary fw-bold">OFFICIAL RESPONSE:</h6>
          </Accordion.Header>
          <Accordion.Body>
            <Container>
              {technicalQuery?.official_response &&
                parse(technicalQuery?.official_response)}
            </Container>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </>
  );
};

export default TQResponseAccordion;
